import type { SerializedStyles } from "@emotion/react";
import { useRef } from "react";
import {
  type AriaCheckboxProps,
  mergeProps,
  useCheckbox,
  useFocusRing,
  VisuallyHidden,
} from "react-aria";
import { useToggleState } from "react-stately";

import { createStaticStyles, cssFns } from "../css";
import { ToggleSwitchCheckedIcon, ToggleSwitchUncheckedIcon } from "../icons";
import { colors } from "../theme";
import { FieldError } from "./common/error";
import { BaseFieldLayout } from "./common/field";
import { type Field, type FieldState, useBaseFieldState } from "./common/form";

export type ToggleSwitchState = FieldState<boolean>;

const toggleSwitchStyles = {
  container: createStaticStyles.clean([
    {
      display: "grid",
      alignItems: "center",
      gridTemplateColumns: "1fr auto",
      gap: 5,
      borderRadius: 4,
      transition: "all 0.2s ease-in-out",
    },
    cssFns.typo({
      level: "body-1",
      weight: "semibold",
      color: colors.principal.gs2,
    }),
  ]),
  input: createStaticStyles.clean({
    position: "relative",
    width: 32,
    height: 20,
  }),
  animation: createStaticStyles.clean({
    position: "absolute",
    transition: "opacity 0.2s ease",
  }),
  visible: createStaticStyles.clean({
    opacity: 1,
  }),
  hidden: createStaticStyles.clean({
    opacity: 0,
  }),
};

export function ToggleSwitch({
  field,
  autoFocus,
  label,
  onChange,
  styles: externalStyles,
}: {
  label?: string;
  field: Field<ToggleSwitchState>;
  autoFocus?: boolean;
  onChange?: (value: boolean) => void;
  styles?: SerializedStyles;
}) {
  const options: AriaCheckboxProps = {
    isRequired: field.required,
    isDisabled: field.disabled,
    autoFocus,
    isSelected: field.value,
    onChange(value) {
      onChange?.(value);
      field.onChange({
        ...field,
        value,
      });
    },
    "aria-label": label ?? "checkbox",
  };

  const state = useToggleState(options);
  const ref = useRef<HTMLInputElement>(null);
  const { inputProps } = useCheckbox(options, state, ref);
  const { focusProps } = useFocusRing();

  return (
    <>
      <BaseFieldLayout
        input={
          <label css={[toggleSwitchStyles.container, ...[externalStyles]]}>
            {label}
            <VisuallyHidden>
              <input {...mergeProps(focusProps, inputProps)} ref={ref} />
            </VisuallyHidden>
            <div css={toggleSwitchStyles.input}>
              <ToggleSwitchCheckedIcon
                css={[
                  toggleSwitchStyles.animation,
                  state.isSelected
                    ? toggleSwitchStyles.visible
                    : toggleSwitchStyles.hidden,
                ]}
              />
              <ToggleSwitchUncheckedIcon
                css={[
                  toggleSwitchStyles.animation,
                  state.isSelected
                    ? toggleSwitchStyles.hidden
                    : toggleSwitchStyles.visible,
                ]}
              />
            </div>
          </label>
        }
        error={
          field.error?.visible && (
            <FieldError
              errorMessage={field.error.message}
              fieldRequired={field.required}
            />
          )
        }
      />
    </>
  );
}

export const useToggleSwitchState = (defaultState?: ToggleSwitchState) => {
  return useBaseFieldState<ToggleSwitchState>({
    value: false,
    error: undefined,
    ...defaultState,
  });
};
